import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaEye, FaFileInvoiceDollar, FaFilePdf, FaMoneyBillWave, FaEdit, FaEnvelope, FaWhatsapp, FaReceipt, FaBan, FaTrash } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { resolveImageUrl } from "../config/apiConfig";
import { downloadInvoicePdf, downloadPaymentReceiptPdf, loadImageAsDataUrl } from "../utils/invoicePdf";
import { archiveInvoicePdf, openStoredInvoicePdf } from "../utils/invoiceArchive";
import { mapCustomerListItemFromApi, getTenantCompanyLabel } from "../utils/formatters";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import BillToPicker from "../components/BillToPicker";
import { financeStatusLabel } from "../utils/financeTerms";

/** Bill To block for PDFs: the invoice's snapshot when present, else the live client details. */
const billedToFromInvoice = (inv, fallback) => {
  const bt = inv?.Bill_To;
  if (!bt || !bt.Name) return fallback;
  return {
    name: bt.Name,
    company: bt.Type === "Organization" ? bt.Name : "",
    contact: bt.Contact || "",
    email: bt.Email || "",
    phone: bt.Phone || "",
    address: bt.Address || "",
    gstin: bt.GSTIN || "",
    pan: bt.PAN || "",
    taxIdType: bt.Tax_Id_Type || "",
    taxId: bt.Tax_Id || ""
  };
};

/** "GSTIN: x · PAN: y" or "EIN: z" for an invoice's Bill_To snapshot. */
const billToTaxLine = (bt) =>
  [
    bt?.GSTIN ? `GSTIN: ${bt.GSTIN}` : "",
    bt?.PAN ? `PAN: ${bt.PAN}` : "",
    bt?.Tax_Id ? `${bt.Tax_Id_Type || "Tax ID"}: ${bt.Tax_Id}` : ""
  ]
    .filter(Boolean)
    .join(" · ");

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const lastDayOfMonthIso = (year, monthIndex) => {
  const d = new Date(year, monthIndex + 1, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseBillingPeriod = (notes) => {
  const m = String(notes || "").match(/Billing period:\s*(.+)/i);
  return m ? m[1].trim() : "";
};

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
};

const displayUser = (name, id) => {
  const n = String(name || "").trim();
  if (n) return n;
  if (id != null && id !== "") return `User #${id}`;
  return "—";
};

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const invoiceHasDues = (inv) => {
  const bal = num(inv?.Balance_Amount);
  const status = String(inv?.Status || "");
  return bal > 0.001 && status !== "Cancelled" && status !== "Paid";
};

/** Drafts are deleted; issued invoices are cancelled, and only while nothing has been paid. */
const canDeleteInvoice = (inv) => String(inv?.Status || "") === "Draft";
const canCancelInvoice = (inv) => {
  const status = String(inv?.Status || "");
  return status !== "Draft" && status !== "Cancelled" && num(inv?.Paid_Amount) <= 0.001;
};

const ARCHIVE_WARNING = "The invoice was saved, but its PDF copy could not be stored. Downloads still work.";

const blankServiceRow = () => ({ selected: false, qty: "1", price: "", description: "" });

/**
 * Generate-form rows for a cancelled invoice's line items, keyed like `selectedServices`.
 * A repeated service name gets a "#n" key; `name` keeps the real Service_Name.
 */
const serviceRowsFromItems = (items = []) => {
  const rows = {};
  items.forEach((it) => {
    const name = String(it.Service_Name || "Service").trim() || "Service";
    let key = name;
    for (let n = 2; rows[key]; n += 1) key = `${name}#${n}`;
    rows[key] = {
      selected: true,
      name,
      qty: String(num(it.Quantity, 1)),
      price: String(num(it.Unit_Price)),
      description: it.Description || ""
    };
  });
  return rows;
};

/** Notes typed on the invoice, without the generated "Billing period:" line. */
const extraNotesOf = (notes) =>
  String(notes || "")
    .split("\n")
    .filter((line) => !/^Billing period:/i.test(line.trim()))
    .join("\n")
    .trim();

const resolveInvoiceContact = (inv, fallbackClient) => {
  const c = fallbackClient || {};
  const email =
    String(
      inv?.Customer_Email ||
        c.Email ||
        c.email ||
        c.Company_Email ||
        c.company_email ||
        ""
    ).trim() || "";
  const phone =
    String(
      inv?.Customer_Phone ||
        c.Phone ||
        c.phone ||
        c.Company_Phone ||
        c.company_phone ||
        ""
    ).trim() || "";
  const countryCode =
    String(
      inv?.Customer_Country_Code || c.Country_Code || c.country_code || c.countryCode || ""
    ).trim() || "";
  return { email, phone, countryCode };
};

const buildPaymentReminderMessage = (inv, tenantLabel, issuer) => {
  const name = String(inv?.Customer_Name || tenantLabel || "Customer").trim();
  const invoiceNo = String(inv?.Invoice_No || "—").trim();
  const currency = String(inv?.Currency_Code || "").trim();
  const balance = formatMoney(inv?.Balance_Amount);
  const amount = currency ? `${currency} ${balance}` : balance;
  const invoiceDate = formatDateDisplay(inv?.Invoice_Date);
  const dueDate = inv?.Due_Date ? formatDateDisplay(inv.Due_Date) : null;
  const period = parseBillingPeriod(inv?.Notes);
  const companyName = String(
    issuer?.displayName ||
      issuer?.Display_Name ||
      issuer?.companyName ||
      issuer?.Company_Name ||
      inv?.Issuer_Company_Name ||
      "Accounts"
  ).trim();
  const accountsEmail = String(
    issuer?.accountsEmail ||
      issuer?.Accounts_Email ||
      inv?.Issuer_Accounts_Email ||
      inv?.Issuer_Company?.Accounts_Email ||
      ""
  ).trim();

  const lines = [
    `Dear ${name},`,
    "",
    `This is a friendly reminder from ${companyName} that invoice ${invoiceNo} (dated ${invoiceDate}) has an outstanding balance of ${amount}.`,
  ];
  if (period) lines.push(`Billing period: ${period}.`);
  if (dueDate && dueDate !== "—") lines.push(`Due date: ${dueDate}.`);
  lines.push("", "Please arrange payment at your earliest convenience.", "");
  if (accountsEmail) {
    lines.push(`Regards,`, `${companyName}`, `Accounts: ${accountsEmail}`);
  } else {
    lines.push(`Regards,`, companyName);
  }
  return lines.join("\n");
};

const resolveInvoiceIssuer = (inv, issuerCompanies = []) => {
  const nested = inv?.Issuer_Company;
  if (nested && (nested.Issuer_Company_Id != null || nested.Company_Name || nested.Accounts_Email)) {
    return nested;
  }
  const id = inv?.Issuer_Company_Id;
  if (id != null && Array.isArray(issuerCompanies)) {
    const found = issuerCompanies.find((c) => String(c.Issuer_Company_Id) === String(id));
    if (found) return found;
  }
  return null;
};

const getInvoiceAccountsEmail = (inv, issuerCompanies = []) => {
  const issuer = resolveInvoiceIssuer(inv, issuerCompanies);
  return (
    String(
      inv?.Issuer_Accounts_Email ||
        issuer?.Accounts_Email ||
        ""
    ).trim() || ""
  );
};

const getInvoiceReminderEmailHref = (inv, tenantLabel, fallbackClient, issuerCompanies = []) => {
  if (!invoiceHasDues(inv)) return null;
  const { email } = resolveInvoiceContact(inv, fallbackClient);
  if (!email || !email.includes("@")) return null;
  const issuer = resolveInvoiceIssuer(inv, issuerCompanies);
  const companyName = String(
    issuer?.Display_Name || issuer?.Company_Name || inv?.Issuer_Company_Name || ""
  ).trim();
  const accountsEmail = getInvoiceAccountsEmail(inv, issuerCompanies);
  const subject = [
    companyName ? `[${companyName}]` : null,
    `Payment reminder — Invoice ${inv?.Invoice_No || ""}`,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const body = buildPaymentReminderMessage(inv, tenantLabel, issuer);
  const params = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`,
  ];
  // BCC accounts mailbox so the configured From identity keeps a copy
  if (accountsEmail && accountsEmail.toLowerCase() !== email.toLowerCase()) {
    params.push(`bcc=${encodeURIComponent(accountsEmail)}`);
  }
  return `mailto:${email}?${params.join("&")}`;
};

const getInvoiceReminderWhatsAppHref = (inv, tenantLabel, fallbackClient, issuerCompanies = []) => {
  if (!invoiceHasDues(inv)) return null;
  const { phone, countryCode } = resolveInvoiceContact(inv, fallbackClient);
  const phoneDigits = digitsOnly(phone);
  if (!phoneDigits) return null;

  let cc = digitsOnly(countryCode);
  let local = phoneDigits;
  if (cc && phoneDigits.startsWith(cc) && phoneDigits.length > cc.length) {
    local = phoneDigits.slice(cc.length);
  } else if (!cc && phoneDigits.length > 10) {
    cc = phoneDigits.slice(0, phoneDigits.length - 10);
    local = phoneDigits.slice(-10);
  }
  if (!cc) return null;

  const e164 = `${cc}${digitsOnly(local)}`;
  if (!e164) return null;
  const issuer = resolveInvoiceIssuer(inv, issuerCompanies);
  const text = buildPaymentReminderMessage(inv, tenantLabel, issuer);
  return `https://wa.me/${e164}?text=${encodeURIComponent(text)}`;
};

const buildReceiptShareMessage = (inv, tenantLabel, issuer, payment) => {
  const name = String(inv?.Customer_Name || tenantLabel || "Customer").trim();
  const invoiceNo = String(inv?.Invoice_No || "—").trim();
  const currency = String(
    payment?.Currency_Code || inv?.Currency_Code || ""
  ).trim();
  const paidRaw = payment?.Amount != null ? payment.Amount : inv?.Paid_Amount;
  const paidLabel = currency ? `${currency} ${formatMoney(paidRaw)}` : formatMoney(paidRaw);
  const payDate = payment?.Payment_Date
    ? formatDateDisplay(payment.Payment_Date)
    : null;
  const mode = String(payment?.Payment_Mode_Name || "").trim();
  const ref = String(payment?.Transaction_Ref || "").trim();
  const balance = formatMoney(inv?.Balance_Amount);
  const balanceLabel = currency ? `${currency} ${balance}` : balance;
  const companyName = String(
    issuer?.displayName ||
      issuer?.Display_Name ||
      issuer?.companyName ||
      issuer?.Company_Name ||
      inv?.Issuer_Company_Name ||
      "Accounts"
  ).trim();
  const accountsEmail = String(
    issuer?.accountsEmail ||
      issuer?.Accounts_Email ||
      inv?.Issuer_Accounts_Email ||
      inv?.Issuer_Company?.Accounts_Email ||
      ""
  ).trim();

  const lines = [
    `Dear ${name},`,
    "",
    `Thank you for your payment of ${paidLabel} toward invoice ${invoiceNo}.`,
  ];
  if (payDate && payDate !== "—") lines.push(`Payment date: ${payDate}.`);
  if (mode) lines.push(`Payment mode: ${mode}.`);
  if (ref) lines.push(`Reference: ${ref}.`);
  lines.push(`Outstanding balance: ${balanceLabel}.`);
  lines.push("", "This message confirms your payment receipt.", "");
  if (accountsEmail) {
    lines.push(`Regards,`, `${companyName}`, `Accounts: ${accountsEmail}`);
  } else {
    lines.push(`Regards,`, companyName);
  }
  return lines.join("\n");
};

const pickLatestPayment = (payments = []) => {
  const completed = (payments || []).filter(
    (p) => String(p.Payment_Status || "Completed").toLowerCase() !== "cancelled"
  );
  if (!completed.length) return null;
  return [...completed].sort((a, b) => {
    const da = new Date(a.Payment_Date || a.Created_On || 0).getTime();
    const db = new Date(b.Payment_Date || b.Created_On || 0).getTime();
    return db - da || Number(b.Payment_Id || 0) - Number(a.Payment_Id || 0);
  })[0];
};

const getReceiptShareEmailHref = (
  inv,
  tenantLabel,
  fallbackClient,
  issuerCompanies = [],
  payment = null
) => {
  if (num(inv?.Paid_Amount) <= 0.001) return null;
  const { email } = resolveInvoiceContact(inv, fallbackClient);
  if (!email || !email.includes("@")) return null;
  const issuer = resolveInvoiceIssuer(inv, issuerCompanies);
  const companyName = String(
    issuer?.Display_Name || issuer?.Company_Name || inv?.Issuer_Company_Name || ""
  ).trim();
  const accountsEmail = getInvoiceAccountsEmail(inv, issuerCompanies);
  const subject = [
    companyName ? `[${companyName}]` : null,
    `Payment receipt — Invoice ${inv?.Invoice_No || ""}`,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const body = buildReceiptShareMessage(inv, tenantLabel, issuer, payment);
  const params = [
    `subject=${encodeURIComponent(subject)}`,
    `body=${encodeURIComponent(body)}`,
  ];
  if (accountsEmail && accountsEmail.toLowerCase() !== email.toLowerCase()) {
    params.push(`bcc=${encodeURIComponent(accountsEmail)}`);
  }
  return `mailto:${email}?${params.join("&")}`;
};

const getReceiptShareWhatsAppHref = (
  inv,
  tenantLabel,
  fallbackClient,
  issuerCompanies = [],
  payment = null
) => {
  if (num(inv?.Paid_Amount) <= 0.001) return null;
  const { phone, countryCode } = resolveInvoiceContact(inv, fallbackClient);
  const phoneDigits = digitsOnly(phone);
  if (!phoneDigits) return null;

  let cc = digitsOnly(countryCode);
  let local = phoneDigits;
  if (cc && phoneDigits.startsWith(cc) && phoneDigits.length > cc.length) {
    local = phoneDigits.slice(cc.length);
  } else if (!cc && phoneDigits.length > 10) {
    cc = phoneDigits.slice(0, phoneDigits.length - 10);
    local = phoneDigits.slice(-10);
  }
  if (!cc) return null;

  const e164 = `${cc}${digitsOnly(local)}`;
  if (!e164) return null;
  const issuer = resolveInvoiceIssuer(inv, issuerCompanies);
  const text = buildReceiptShareMessage(inv, tenantLabel, issuer, payment);
  return `https://wa.me/${e164}?text=${encodeURIComponent(text)}`;
};

/**
 * Invoices & Payments panel — used on tenant detail and Finance (cross-tenant).
 * @param {"tenant"|"finance"} [scope]
 * @param {string|number} [filterClientId] - Finance: filter list to one tenant
 * @param {function} [onFilterClientIdChange]
 */
function TenantInvoicesTab({
  clientId: propClientId,
  client: propClient,
  addressStr: propAddressStr,
  services: propServices,
  scope = "tenant",
  filterClientId = "",
  onFilterClientIdChange
}) {
  const isFinance = scope === "finance";
  const [tenants, setTenants] = useState([]);
  const [generateClientId, setGenerateClientId] = useState("");
  const [generateClient, setGenerateClient] = useState(null);
  const [generateAddressStr, setGenerateAddressStr] = useState("");
  const [generateServices, setGenerateServices] = useState([]);
  const [loadingGenerateClient, setLoadingGenerateClient] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  const listClientId = isFinance
    ? filterClientId
      ? String(filterClientId)
      : ""
    : propClientId != null
      ? String(propClientId)
      : "";

  const activeGenerateClientId = isFinance
    ? generateClientId
    : propClientId != null
      ? String(propClientId)
      : "";

  const client = isFinance ? generateClient : propClient;
  const addressStr = isFinance ? generateAddressStr : propAddressStr;
  const services = isFinance ? generateServices : propServices;

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [paymentsByAr, setPaymentsByAr] = useState({});
  const [loadingPayments, setLoadingPayments] = useState({});

  const [showGenerate, setShowGenerate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  /** { type: "delete" | "cancel", invoice } while the reason dialog is open. */
  const [reasonAction, setReasonAction] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [replaceAfterCancel, setReplaceAfterCancel] = useState(true);
  /** Cancelled invoice the generate form is replacing (pre-filled from it), or null. */
  const [replaceSource, setReplaceSource] = useState(null);
  const prefillServicesRef = useRef(null);
  const [notice, setNotice] = useState("");

  const [paymentModes, setPaymentModes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [issuerCompanies, setIssuerCompanies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [loadingIssuers, setLoadingIssuers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const now = new Date();
  const [billingMonth, setBillingMonth] = useState(now.getMonth());
  const [billingYear, setBillingYear] = useState(now.getFullYear());
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(lastDayOfMonthIso(now.getFullYear(), now.getMonth()));
  const [currencyId, setCurrencyId] = useState("");
  const [issuerCompanyId, setIssuerCompanyId] = useState("");
  const [billToId, setBillToId] = useState("");
  const [taxPercent, setTaxPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [selectedServices, setSelectedServices] = useState({});
  const [customLine, setCustomLine] = useState({ enabled: false, name: "", qty: "1", price: "", description: "" });

  const [showEditIssuer, setShowEditIssuer] = useState(false);
  const [issuerEditForm, setIssuerEditForm] = useState({
    Company_Name: "",
    Display_Name: "",
    Address_Line: "",
    Phone: "",
    Email: "",
    Accounts_Email: "",
    Website: "",
    Logo_Url: ""
  });

  const [payForm, setPayForm] = useState({
    amount: "",
    paymentDate: todayIso(),
    paymentModeId: "",
    currencyId: "",
    transactionRef: "",
    notes: ""
  });

  const defaultCurrencyId = useMemo(() => {
    const inr = currencies.find((c) => String(c.code || "").toUpperCase() === "INR" || c.symbol === "₹");
    return inr?.id != null ? String(inr.id) : currencies[0]?.id != null ? String(currencies[0].id) : "";
  }, [currencies]);

  const defaultIssuerId = useMemo(() => {
    const def = issuerCompanies.find((c) => c.Is_Default);
    const first = issuerCompanies[0];
    const id = def?.Issuer_Company_Id ?? first?.Issuer_Company_Id;
    return id != null ? String(id) : "";
  }, [issuerCompanies]);

  const selectedIssuer = useMemo(
    () => issuerCompanies.find((c) => String(c.Issuer_Company_Id) === String(issuerCompanyId)) || null,
    [issuerCompanies, issuerCompanyId]
  );

  const currencyLabel = (id, codeFallback) => {
    if (codeFallback) return codeFallback;
    const c = currencies.find((x) => String(x.id) === String(id));
    if (!c) return "";
    return c.code || c.symbol || c.name || "";
  };

  const issuerLabel = (co) => {
    if (!co) return "—";
    return co.Display_Name || co.Company_Name || co.Company_Code || "—";
  };

  const tenantLabelById = useMemo(() => {
    const map = new Map();
    tenants.forEach((t) => map.set(String(t.id), getTenantCompanyLabel(t)));
    return map;
  }, [tenants]);

  const serviceList = useMemo(() => {
    if (!Array.isArray(services)) return [];
    return services.map((s) => (typeof s === "string" ? s : s?.name || s?.Service_Name || "")).filter(Boolean);
  }, [services]);

  const clientDisplay = useMemo(() => {
    const first = client?.First_Name || client?.first_name || "";
    const last = client?.Last_Name || client?.last_name || "";
    const name = [first, last].filter(Boolean).join(" ").trim();
    return {
      name: name || "—",
      company: client?.Company_Name || client?.company_name || "",
      email: client?.Email || client?.Company_Email || client?.email || "",
      phone: client?.Phone || client?.Company_Phone || client?.phone || "",
      address: addressStr || ""
    };
  }, [client, addressStr]);

  const fetchInvoices = useCallback(async () => {
    if (!listClientId) {
      setInvoices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = { include_items: true, client_id: listClientId };
      const res = await axiosInstance.get("/fico/accounts-receivable", { params });
      const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setInvoices(rows);
    } catch (err) {
      console.error("Failed to load invoices:", err);
      setError(err.response?.data?.error || "Failed to load invoices.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [listClientId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (!isFinance) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("/clients").catch(() => ({ data: [] }));
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const mapped = raw
          .map((item) => mapCustomerListItemFromApi(item))
          .filter(Boolean)
          .sort((a, b) =>
            getTenantCompanyLabel(a).localeCompare(getTenantCompanyLabel(b), undefined, { sensitivity: "base" })
          );
        if (!cancelled) setTenants(mapped);
      } catch {
        if (!cancelled) setTenants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isFinance]);

  const loadClientForGenerate = useCallback(async (cid) => {
    if (!cid) {
      setGenerateClient(null);
      setGenerateAddressStr("");
      setGenerateServices([]);
      return;
    }
    setLoadingGenerateClient(true);
    try {
      const res = await axiosInstance.get(`/client/${cid}`);
      const data = res.data || {};
      const c = data.client || data;
      const companyAddress = data.company_address || {};
      const address = data.address || {};
      const preferCompany = !!(
        companyAddress.Address_Line_1 ||
        companyAddress.City_Id ||
        companyAddress.Zip_Code
      );
      const displayAddress = preferCompany ? companyAddress : address;
      const parts = [
        displayAddress.Address_Line_1,
        displayAddress.Address_Line_2,
        displayAddress.City_Name,
        displayAddress.State_Name,
        displayAddress.Country_Name,
        displayAddress.Zip_Code
      ].filter(Boolean);
      setGenerateClient(c);
      setGenerateAddressStr(parts.length ? parts.join(", ") : "");
      setGenerateServices(Array.isArray(c.Services) ? c.Services : []);
    } catch (err) {
      console.error("Failed to load client for invoice:", err);
      setGenerateClient(null);
      setGenerateAddressStr("");
      setGenerateServices([]);
      setError(err.response?.data?.error || "Failed to load client details.");
    } finally {
      setLoadingGenerateClient(false);
    }
  }, []);

  useEffect(() => {
    setDueDate(lastDayOfMonthIso(billingYear, billingMonth));
  }, [billingMonth, billingYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [modeRes, currRes, issuerRes] = await Promise.all([
          axiosInstance.get("/payment-modes").catch(() => ({ data: [] })),
          axiosInstance.get("/currencies").catch(() => ({ data: [] })),
          axiosInstance.get("/fico/issuer-companies", { params: { status: "Active" } }).catch(() => ({ data: [] }))
        ]);
        const rawModes = Array.isArray(modeRes.data)
          ? modeRes.data
          : modeRes.data?.data ?? modeRes.data?.payment_modes ?? [];
        const modeList =
          rawModes.length > 0
            ? rawModes.map((m) => ({
                id: m.Payment_Mode_Id ?? m.payment_mode_id ?? m.id,
                name: m.Payment_Mode_Name ?? m.payment_mode_name ?? m.name ?? ""
              }))
            : [
                { id: 1, name: "Cash" },
                { id: 2, name: "Bank Transfer" },
                { id: 3, name: "UPI" },
                { id: 4, name: "Cheque" }
              ];

        const rawCurr = Array.isArray(currRes.data)
          ? currRes.data
          : currRes.data?.data ?? currRes.data?.currencies ?? [];
        const fallbackList = [
          { id: 1, name: "Indian Rupee", code: "INR", symbol: "₹" },
          { id: 2, name: "US Dollar", code: "USD", symbol: "$" }
        ];
        const currList =
          rawCurr.length > 0
            ? rawCurr.map((c) => ({
                id: c.Currency_Id ?? c.currency_id ?? c.id,
                name: c.Currency_Name ?? c.currency_name ?? c.name ?? "",
                code: c.Currency_Code ?? c.currency_code ?? c.code ?? "",
                symbol: c.Currency_Symbol ?? c.currency_symbol ?? c.symbol ?? ""
              }))
            : fallbackList;
        const filtered = currList.filter((c) => {
          const code = String(c?.code ?? "").trim().toUpperCase();
          const symbol = String(c?.symbol ?? "").trim();
          const isInr = code === "INR" || symbol === "₹";
          const isUsd = code === "USD" || (code === "" && symbol === "$");
          return isInr || isUsd;
        });

        const rawIssuers = Array.isArray(issuerRes.data) ? issuerRes.data : [];

        if (!cancelled) {
          setPaymentModes(modeList);
          setCurrencies(filtered.length > 0 ? filtered : fallbackList);
          setIssuerCompanies(rawIssuers);
        }
      } catch {
        if (!cancelled) {
          setPaymentModes([
            { id: 1, name: "Cash" },
            { id: 2, name: "Bank Transfer" },
            { id: 3, name: "UPI" },
            { id: 4, name: "Cheque" }
          ]);
          setCurrencies([
            { id: 1, name: "Indian Rupee", code: "INR", symbol: "₹" },
            { id: 2, name: "US Dollar", code: "USD", symbol: "$" }
          ]);
          setIssuerCompanies([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingCurrencies(false);
          setLoadingIssuers(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currencyId && defaultCurrencyId) setCurrencyId(defaultCurrencyId);
  }, [currencyId, defaultCurrencyId]);

  useEffect(() => {
    if (!issuerCompanyId && defaultIssuerId) setIssuerCompanyId(defaultIssuerId);
  }, [issuerCompanyId, defaultIssuerId]);

  const refreshIssuerCompanies = async () => {
    try {
      const res = await axiosInstance.get("/fico/issuer-companies", { params: { status: "Active" } });
      const rows = Array.isArray(res.data) ? res.data : [];
      setIssuerCompanies(rows);
      return rows;
    } catch (err) {
      console.error("Failed to refresh issuer companies:", err);
      return issuerCompanies;
    }
  };

  const fetchPaymentsForAr = async (arId) => {
    if (!arId) return;
    setLoadingPayments((p) => ({ ...p, [arId]: true }));
    try {
      const res = await axiosInstance.get("/fico/ar-payments", { params: { ar_id: arId } });
      const rows = Array.isArray(res.data) ? res.data : [];
      setPaymentsByAr((p) => ({ ...p, [arId]: rows }));
    } catch (err) {
      console.error("Failed to load payments:", err);
      setPaymentsByAr((p) => ({ ...p, [arId]: [] }));
    } finally {
      setLoadingPayments((p) => ({ ...p, [arId]: false }));
    }
  };

  const toggleExpand = (arId) => {
    if (expandedId === arId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(arId);
    if (!paymentsByAr[arId]) fetchPaymentsForAr(arId);
  };

  /** Blank rows for the client's services, with any replacement pre-fill on top. */
  const initialServiceRows = useCallback(
    (names) => {
      const init = {};
      names.forEach((name) => {
        init[name] = blankServiceRow();
      });
      return { ...init, ...(prefillServicesRef.current || {}) };
    },
    []
  );

  /** Open the generate form; with `source` (a cancelled invoice) it is pre-filled as its replacement. */
  const openGenerate = async (source = null) => {
    const initCid = source
      ? String(source.client_id ?? "")
      : isFinance
        ? filterClientId || generateClientId || (tenants[0]?.id != null ? String(tenants[0].id) : "")
        : propClientId != null
          ? String(propClientId)
          : "";
    prefillServicesRef.current = source ? serviceRowsFromItems(source.items) : null;
    setReplaceSource(source);
    if (isFinance) {
      setGenerateClientId(initCid);
      await loadClientForGenerate(initCid);
    }
    setSelectedServices(initialServiceRows(serviceList));
    setCustomLine({ enabled: false, name: "", qty: "1", price: "", description: "" });
    setError("");
    setInvoiceDate(todayIso());
    if (source) {
      const firstTax = (source.items || []).find((it) => it.Tax_Percentage != null)?.Tax_Percentage;
      setTaxPercent(firstTax != null ? String(num(firstTax)) : "");
      setDiscountAmount(num(source.Discount_Amount) ? String(num(source.Discount_Amount)) : "");
      setExtraNotes(extraNotesOf(source.Notes));
      setCurrencyId(source.Currency_Id != null ? String(source.Currency_Id) : defaultCurrencyId);
      setIssuerCompanyId(source.Issuer_Company_Id != null ? String(source.Issuer_Company_Id) : defaultIssuerId);
      setBillToId(source.Bill_To_Id != null ? String(source.Bill_To_Id) : "");
      const [monthName, year] = parseBillingPeriod(source.Notes).split(/\s+/);
      const monthIdx = MONTH_NAMES.indexOf(monthName);
      const month = monthIdx >= 0 ? monthIdx : now.getMonth();
      const yr = Number(year) || now.getFullYear();
      setBillingMonth(month);
      setBillingYear(yr);
      setDueDate(lastDayOfMonthIso(yr, month));
    } else {
      setTaxPercent("");
      setDiscountAmount("");
      setExtraNotes("");
      setCurrencyId(defaultCurrencyId);
      setIssuerCompanyId(defaultIssuerId);
      setBillToId("");
      setBillingMonth(now.getMonth());
      setBillingYear(now.getFullYear());
      setDueDate(lastDayOfMonthIso(now.getFullYear(), now.getMonth()));
    }
    setShowGenerate(true);
  };

  const closeGenerate = () => {
    if (submitting) return;
    setShowGenerate(false);
    setReplaceSource(null);
    prefillServicesRef.current = null;
  };

  useEffect(() => {
    if (!showGenerate || !isFinance) return;
    setSelectedServices(initialServiceRows(serviceList));
  }, [showGenerate, isFinance, serviceList, initialServiceRows]);

  /** Client services first, then pre-filled lines that are not one of them. */
  const formServiceKeys = useMemo(
    () => [...serviceList, ...Object.keys(selectedServices).filter((k) => !serviceList.includes(k))],
    [serviceList, selectedServices]
  );

  const openEditIssuer = () => {
    const co = selectedIssuer;
    if (!co) return;
    setIssuerEditForm({
      Company_Name: co.Company_Name || "",
      Display_Name: co.Display_Name || "",
      Address_Line: co.Address_Line || "",
      Phone: co.Phone || "",
      Email: co.Email || "",
      Accounts_Email: co.Accounts_Email || "",
      Website: co.Website || "",
      Logo_Url: co.Logo_Url || ""
    });
    setError("");
    setShowEditIssuer(true);
  };

  const handleSaveIssuer = async (e) => {
    e.preventDefault();
    if (!issuerCompanyId) return;
    if (!issuerEditForm.Company_Name.trim()) {
      setError("Company name is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axiosInstance.put(`/fico/issuer-companies/${issuerCompanyId}`, {
        Company_Name: issuerEditForm.Company_Name.trim(),
        Display_Name: issuerEditForm.Display_Name.trim() || null,
        Address_Line: issuerEditForm.Address_Line.trim() || null,
        Phone: issuerEditForm.Phone.trim() || null,
        Email: issuerEditForm.Email.trim() || null,
        Accounts_Email: issuerEditForm.Accounts_Email.trim() || null,
        Website: issuerEditForm.Website.trim() || null,
        Logo_Url: issuerEditForm.Logo_Url.trim() || null
      });
      await refreshIssuerCompanies();
      setShowEditIssuer(false);
    } catch (err) {
      console.error("Failed to update issuer company:", err);
      setError(err.response?.data?.error || "Failed to update company details.");
    } finally {
      setSubmitting(false);
    }
  };

  const lineItemsPreview = useMemo(() => {
    const items = [];
    Object.entries(selectedServices).forEach(([name, row]) => {
      if (!row.selected) return;
      const qty = num(row.qty, 1);
      const price = num(row.price, 0);
      items.push({
        Service_Name: row.name || name,
        Description: row.description || null,
        Quantity: qty,
        Unit_Price: price,
        Tax_Percentage: taxPercent !== "" ? num(taxPercent) : undefined
      });
    });
    if (customLine.enabled && customLine.name.trim()) {
      items.push({
        Service_Name: customLine.name.trim(),
        Description: customLine.description || null,
        Quantity: num(customLine.qty, 1),
        Unit_Price: num(customLine.price, 0),
        Tax_Percentage: taxPercent !== "" ? num(taxPercent) : undefined
      });
    }
    return items;
  }, [selectedServices, customLine, taxPercent]);

  const totalsPreview = useMemo(() => {
    let sub = 0;
    let tax = 0;
    const tp = taxPercent !== "" ? num(taxPercent) : 0;
    lineItemsPreview.forEach((it) => {
      const base = num(it.Quantity) * num(it.Unit_Price);
      sub += base;
      tax += (base * tp) / 100;
    });
    const discount = num(discountAmount);
    const total = Math.max(0, sub + tax - discount);
    return { sub, tax, discount, total };
  }, [lineItemsPreview, taxPercent, discountAmount]);

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    const submitClientId = activeGenerateClientId;
    if (!submitClientId) {
      setError("Client is required.");
      return;
    }
    if (lineItemsPreview.length === 0) {
      setError("Select at least one service or add a custom line.");
      return;
    }
    if (lineItemsPreview.some((it) => num(it.Unit_Price) < 0)) {
      setError("Unit price cannot be negative.");
      return;
    }
    if (taxPercent !== "" && (num(taxPercent, -1) < 0 || num(taxPercent) > 100)) {
      setError("Tax % must be between 0 and 100.");
      return;
    }
    if (!currencyId) {
      setError("Currency is required.");
      return;
    }
    if (!issuerCompanyId) {
      setError("Issuer company is required.");
      return;
    }
    if (!billToId) {
      setError("Bill To is required.");
      return;
    }

    const periodLabel = `${MONTH_NAMES[billingMonth]} ${billingYear}`;
    const notesParts = [`Billing period: ${periodLabel}`];
    if (extraNotes.trim()) notesParts.push(extraNotes.trim());

    const items = lineItemsPreview.map((it) => {
      const payload = {
        Service_Name: it.Service_Name,
        Description: it.Description,
        Quantity: it.Quantity,
        Unit_Price: it.Unit_Price
      };
      if (it.Tax_Percentage != null && it.Tax_Percentage !== undefined && taxPercent !== "") {
        payload.Tax_Percentage = it.Tax_Percentage;
      }
      return payload;
    });

    setSubmitting(true);
    setError("");
    try {
      const res = await axiosInstance.post("/fico/accounts-receivable", {
        client_id: Number(submitClientId),
        Invoice_Date: invoiceDate,
        Due_Date: dueDate || null,
        Currency_Id: Number(currencyId),
        Issuer_Company_Id: Number(issuerCompanyId),
        Bill_To_Id: Number(billToId),
        Discount_Amount: num(discountAmount),
        Status: "Sent",
        Notes: notesParts.join("\n"),
        items,
        ...(replaceSource?.AR_Id ? { Replaces_AR_Id: replaceSource.AR_Id } : {})
      });
      const created = res?.data;
      if (created?.AR_Id) {
        const stored = await archiveInvoicePdf(created.AR_Id, await buildPdfData(created, []), "Issued");
        setNotice(stored ? "" : ARCHIVE_WARNING);
      }
      setShowGenerate(false);
      setReplaceSource(null);
      prefillServicesRef.current = null;
      if (isFinance && String(submitClientId) !== listClientId && onFilterClientIdChange) {
        // The finance list only loads one client's invoices; show the one just invoiced.
        onFilterClientIdChange(String(submitClientId));
      } else {
        await fetchInvoices();
      }
    } catch (err) {
      console.error("Failed to create invoice:", err);
      setError(err.response?.data?.error || err.response?.data?.details || "Failed to create invoice.");
    } finally {
      setSubmitting(false);
    }
  };

  const openPayment = (inv) => {
    const balance = num(inv.Balance_Amount);
    const invCurrency =
      inv.Currency_Id != null && inv.Currency_Id !== ""
        ? String(inv.Currency_Id)
        : defaultCurrencyId;
    setPaymentSuccess(null);
    setPaymentInvoice(inv);
    setPayForm({
      amount: balance > 0 ? String(balance) : "",
      paymentDate: todayIso(),
      paymentModeId: paymentModes[0]?.id ? String(paymentModes[0].id) : "",
      currencyId: invCurrency,
      transactionRef: "",
      notes: ""
    });
    setError("");
    setShowPayment(true);
  };

  const closePaymentModal = () => {
    if (submitting || receiptBusy) return;
    setShowPayment(false);
    setPaymentInvoice(null);
    setPaymentSuccess(null);
    setError("");
  };

  const buildPaymentReceiptData = async (inv, paymentRow, amountPaid) => {
    let fullInv = inv;
    if (!Array.isArray(inv?.items) || !inv?.Issuer_Company) {
      try {
        const res = await axiosInstance.get(`/fico/accounts-receivable/${inv.AR_Id}`);
        fullInv = res.data || inv;
      } catch {
        fullInv = inv;
      }
    }
    const base = await buildPdfData(fullInv, []);
    const modeName =
      paymentRow?.Payment_Mode_Name ||
      paymentModes.find((m) => String(m.id) === String(paymentRow?.Payment_Mode_Id || payForm.paymentModeId))
        ?.name ||
      "";
    const paidNow = Number(amountPaid) || Number(paymentRow?.Amount) || 0;
    const prevPaid = num(inv.Paid_Amount);
    const invTotal = num(inv.Total_Amount);
    const newPaid = prevPaid + paidNow;
    const newBalance = Math.max(invTotal - newPaid, 0);
    return {
      payment: {
        paymentId: paymentRow?.Payment_Id,
        paymentDate: paymentRow?.Payment_Date || payForm.paymentDate,
        amount: paidNow,
        currency:
          paymentRow?.Currency_Code ||
          inv.Currency_Code ||
          currencyLabel(paymentRow?.Currency_Id || payForm.currencyId || inv.Currency_Id),
        mode: modeName,
        ref: paymentRow?.Transaction_Ref || payForm.transactionRef.trim() || "",
        notes: paymentRow?.Notes || payForm.notes.trim() || "",
        status: paymentRow?.Payment_Status || "Completed"
      },
      invoice: {
        invoiceNo: fullInv.Invoice_No || inv.Invoice_No,
        invoiceDate: fullInv.Invoice_Date || inv.Invoice_Date,
        currency:
          paymentRow?.Currency_Code ||
          inv.Currency_Code ||
          currencyLabel(inv.Currency_Id),
        totalAmount: invTotal,
        paidAmount: newPaid,
        balanceAmount: newBalance
      },
      billedTo: base.billedTo,
      issuer: base.issuer
    };
  };

  const downloadReceiptForPayment = async (inv, paymentRow, amountPaid) => {
    const receiptData = await buildPaymentReceiptData(inv, paymentRow, amountPaid);
    downloadPaymentReceiptPdf(
      receiptData,
      `receipt-${receiptData.invoice.invoiceNo || inv.AR_Id}-${receiptData.payment.paymentId || "pay"}.pdf`
    );
    return receiptData;
  };

  const downloadInvoiceForPayment = async (inv, paymentRow, amountPaid) => {
    let fullInv = inv;
    try {
      const res = await axiosInstance.get(`/fico/accounts-receivable/${inv.AR_Id}`);
      if (res.data) fullInv = res.data;
    } catch {
      /* keep snapshot */
    }

    let payments = [];
    try {
      const res = await axiosInstance.get("/fico/ar-payments", { params: { ar_id: inv.AR_Id } });
      payments = Array.isArray(res.data) ? res.data : [];
    } catch {
      payments = Array.isArray(paymentsByAr[inv.AR_Id]) ? paymentsByAr[inv.AR_Id] : [];
    }
    if (paymentRow?.Payment_Id && !payments.some((p) => String(p.Payment_Id) === String(paymentRow.Payment_Id))) {
      payments = [paymentRow, ...payments];
    }

    const paidNow = Number(amountPaid) || Number(paymentRow?.Amount) || 0;
    const prevPaid = num(inv.Paid_Amount);
    const invTotal = num(fullInv.Total_Amount ?? inv.Total_Amount);
    const newPaid = num(fullInv.Paid_Amount) > prevPaid ? num(fullInv.Paid_Amount) : prevPaid + paidNow;
    const newBalance =
      fullInv.Balance_Amount != null && Number(fullInv.Balance_Amount) < num(inv.Balance_Amount)
        ? num(fullInv.Balance_Amount)
        : Math.max(invTotal - newPaid, 0);

    const pdfData = await buildPdfData(
      {
        ...fullInv,
        Paid_Amount: newPaid,
        Balance_Amount: newBalance
      },
      payments
    );
    downloadInvoicePdf(pdfData, `invoice-${fullInv.Invoice_No || inv.AR_Id}.pdf`);
    return pdfData;
  };

  const downloadPaymentPdfs = async (inv, paymentRow, amountPaid) => {
    const receiptData = await downloadReceiptForPayment(inv, paymentRow, amountPaid);
    // Allow the browser to finish the first download before starting the second
    await new Promise((r) => setTimeout(r, 350));
    await downloadInvoiceForPayment(inv, paymentRow, amountPaid);
    return receiptData;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    const payClientId = paymentInvoice?.client_id ?? paymentInvoice?.Client_Id ?? propClientId;
    if (!paymentInvoice?.AR_Id || !payClientId) return;
    const amount = num(payForm.amount);
    const balance = num(paymentInvoice.Balance_Amount);
    if (amount <= 0) {
      setError("Amount must be greater than 0.");
      return;
    }
    if (balance > 0 && amount > balance + 0.001) {
      setError(`Amount cannot exceed balance (${formatMoney(balance)}).`);
      return;
    }
    if (!payForm.paymentDate) {
      setError("Payment date is required.");
      return;
    }

    const invSnapshot = paymentInvoice;
    setSubmitting(true);
    setError("");
    try {
      const res = await axiosInstance.post("/fico/ar-payment", {
        AR_Id: invSnapshot.AR_Id,
        client_id: Number(payClientId),
        Amount: amount,
        Payment_Date: payForm.paymentDate,
        Currency_Id: payForm.currencyId ? Number(payForm.currencyId) : null,
        Payment_Mode_Id: payForm.paymentModeId ? Number(payForm.paymentModeId) : null,
        Transaction_Ref: payForm.transactionRef.trim() || null,
        Notes: payForm.notes.trim() || null,
        Payment_Status: "Completed"
      });
      const paymentRow = res?.data || {};
      await fetchInvoices();
      await fetchPaymentsForAr(invSnapshot.AR_Id);

      let receiptData = null;
      setReceiptBusy(true);
      try {
        receiptData = await downloadPaymentPdfs(invSnapshot, paymentRow, amount);
      } catch (receiptErr) {
        console.error("Failed to generate payment PDFs:", receiptErr);
      } finally {
        setReceiptBusy(false);
      }

      setPaymentSuccess({
        invoice: invSnapshot,
        payment: paymentRow,
        amount,
        receiptData,
        invoiceDownloaded: Boolean(receiptData)
      });
    } catch (err) {
      console.error("Failed to record payment:", err);
      setError(err.response?.data?.error || "Failed to record payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const buildPdfData = async (inv, payments = []) => {
    const items = Array.isArray(inv.items) ? inv.items : [];
    const issuer =
      inv.Issuer_Company ||
      issuerCompanies.find((c) => String(c.Issuer_Company_Id) === String(inv.Issuer_Company_Id)) ||
      null;
    let logoDataUrl = null;
    if (issuer?.Logo_Url) {
      logoDataUrl = await loadImageAsDataUrl(resolveImageUrl(issuer.Logo_Url));
    }
    return {
      invoice: {
        invoiceNo: inv.Invoice_No,
        invoiceDate: inv.Invoice_Date,
        dueDate: inv.Due_Date,
        billingPeriod: parseBillingPeriod(inv.Notes),
        currency: inv.Currency_Code || currencyLabel(inv.Currency_Id),
        notes: inv.Notes,
        subTotal: inv.Sub_Total,
        taxAmount: inv.Tax_Amount,
        discountAmount: inv.Discount_Amount,
        totalAmount: inv.Total_Amount,
        paidAmount: inv.Paid_Amount,
        balanceAmount: inv.Balance_Amount,
        replacesInvoiceNo: inv.Replaces_Invoice_No
      },
      billedTo: billedToFromInvoice(
        inv,
        isFinance
          ? {
              name:
                inv.Customer_Name ||
                tenantLabelById.get(String(inv.client_id ?? "")) ||
                clientDisplay.name,
              company: clientDisplay.company,
              email: clientDisplay.email,
              phone: clientDisplay.phone,
              address: clientDisplay.address
            }
          : clientDisplay
      ),
      issuer: issuer
        ? {
            companyCode: issuer.Company_Code,
            companyName: issuer.Company_Name,
            displayName: issuer.Display_Name,
            address: issuer.Address_Line,
            phone: issuer.Phone,
            email: issuer.Email,
            website: issuer.Website,
            logoDataUrl
          }
        : null,
      lineItems: items.map((it) => ({
        item: it.Service_Name,
        description: it.Description,
        quantity: num(it.Quantity, 1),
        rate: num(it.Unit_Price),
        amount: num(it.Total_Amount, num(it.Quantity, 1) * num(it.Unit_Price))
      })),
      payments: (payments || []).map((p) => ({
        date: p.Payment_Date,
        amount: p.Amount,
        mode: p.Payment_Mode_Name,
        ref: p.Transaction_Ref
      })),
      cancelled:
        inv.Status === "Cancelled"
          ? { reason: inv.Cancel_Reason, date: inv.Cancelled_On ? formatDateDisplay(inv.Cancelled_On) : "" }
          : null
    };
  };

  const openReasonDialog = (type, inv) => {
    setError("");
    setReasonText("");
    setReplaceAfterCancel(true);
    setReasonAction({ type, invoice: inv });
  };

  const closeReasonDialog = () => {
    if (submitting) return;
    setReasonAction(null);
    setReasonText("");
    setError("");
  };

  const handleReasonSubmit = async (e) => {
    e.preventDefault();
    const inv = reasonAction?.invoice;
    const reason = reasonText.trim();
    if (!inv?.AR_Id) return;
    if (!reason) {
      setError("Please enter a reason.");
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    let replaceWith = null;
    try {
      if (reasonAction.type === "delete") {
        await axiosInstance.delete(`/fico/delete-accounts-receivable/${inv.AR_Id}`, { data: { reason } });
        if (viewInvoice?.AR_Id === inv.AR_Id) setViewInvoice(null);
      } else {
        const res = await axiosInstance.post(`/fico/accounts-receivable/${inv.AR_Id}/cancel`, { reason });
        const updated = res?.data || { ...inv, Status: "Cancelled", Cancel_Reason: reason };
        const stored = await archiveInvoicePdf(
          inv.AR_Id,
          await buildPdfData(updated, paymentsByAr[inv.AR_Id] || []),
          "Cancelled"
        );
        if (!stored) setNotice(ARCHIVE_WARNING);
        if (replaceAfterCancel) {
          replaceWith = updated;
          setViewInvoice(null);
        } else if (viewInvoice?.AR_Id === inv.AR_Id) {
          const fresh = await axiosInstance.get(`/fico/accounts-receivable/${inv.AR_Id}`).catch(() => null);
          setViewInvoice(fresh?.data || updated);
        }
      }
      setReasonAction(null);
      setReasonText("");
      await fetchInvoices();
    } catch (err) {
      console.error(`Failed to ${reasonAction.type} invoice:`, err);
      setError(err.response?.data?.error || `Failed to ${reasonAction.type} invoice.`);
    } finally {
      setSubmitting(false);
    }
    if (replaceWith) await openGenerate(replaceWith);
  };

  /** "Create replacement" on a cancelled invoice that has none yet. */
  const openReplacement = async (inv) => {
    let full = inv;
    try {
      const res = await axiosInstance.get(`/fico/accounts-receivable/${inv.AR_Id}`);
      if (res.data) full = res.data;
    } catch {
      /* use what we have */
    }
    setViewInvoice(null);
    await openGenerate(full);
  };

  const handleDownloadPdf = async (inv) => {
    let payments = paymentsByAr[inv.AR_Id];
    if (!payments) {
      try {
        const res = await axiosInstance.get("/fico/ar-payments", { params: { ar_id: inv.AR_Id } });
        payments = Array.isArray(res.data) ? res.data : [];
        setPaymentsByAr((p) => ({ ...p, [inv.AR_Id]: payments }));
      } catch {
        payments = [];
      }
    }
    let fullInv = inv;
    if (!Array.isArray(inv.items) || !inv.Issuer_Company) {
      try {
        const res = await axiosInstance.get(`/fico/accounts-receivable/${inv.AR_Id}`);
        fullInv = res.data || inv;
      } catch {
        fullInv = inv;
      }
    }
    const pdfData = await buildPdfData(fullInv, payments);
    downloadInvoicePdf(pdfData, `invoice-${fullInv.Invoice_No || fullInv.AR_Id}.pdf`);
  };

  const handleDownloadReceiptPdf = async (inv) => {
    let payments = paymentsByAr[inv.AR_Id];
    if (!payments) {
      try {
        const res = await axiosInstance.get("/fico/ar-payments", { params: { ar_id: inv.AR_Id } });
        payments = Array.isArray(res.data) ? res.data : [];
        setPaymentsByAr((p) => ({ ...p, [inv.AR_Id]: payments }));
      } catch {
        payments = [];
      }
    }
    const completed = (payments || []).filter(
      (p) => String(p.Payment_Status || "Completed").toLowerCase() !== "cancelled"
    );
    if (!completed.length) {
      setError("No payments recorded for this invoice yet.");
      return;
    }
    const latest = pickLatestPayment(completed);
    if (!latest) {
      setError("No payments recorded for this invoice yet.");
      return;
    }
    setError("");
    await downloadReceiptForPayment(inv, latest, latest.Amount);
  };

  const invoiceHasReceipt = (inv) => num(inv.Paid_Amount) > 0.001;

  const openView = async (inv) => {
    setError("");
    try {
      const res = await axiosInstance.get(`/fico/accounts-receivable/${inv.AR_Id}`);
      setViewInvoice(res.data || inv);
    } catch {
      setViewInvoice(inv);
    }
    if (!paymentsByAr[inv.AR_Id]) fetchPaymentsForAr(inv.AR_Id);
  };

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
  }, [now]);

  const filteredInvoices = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return invoices.filter((inv) => {
      const status = String(inv.Status || "");
      const bal = Number(inv.Balance_Amount) || 0;
      if (statusFilter === "unpaid") {
        if (!(bal > 0.001 && status !== "Cancelled" && status !== "Paid")) return false;
      } else if (statusFilter === "paid") {
        if (status !== "Paid" && !(bal <= 0.001 && status !== "Cancelled")) return false;
      } else if (statusFilter === "partial") {
        if (status !== "Partially Paid") return false;
      }
      if (!q) return true;
      const tenantName = (
        inv.Customer_Name ||
        tenantLabelById.get(String(inv.client_id ?? "")) ||
        ""
      ).toLowerCase();
      const hay = [
        inv.Invoice_No,
        tenantName,
        parseBillingPeriod(inv.Notes),
        inv.Issuer_Company_Name,
        inv.Status
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [invoices, statusFilter, searchText, tenantLabelById]);

  const resolveShareContactClient = isFinance ? null : propClient;

  const viewReceiptShare = useMemo(() => {
    if (!viewInvoice || num(viewInvoice.Paid_Amount) <= 0.001) {
      return { emailHref: null, whatsappHref: null };
    }
    const tenantLabel =
      viewInvoice.Customer_Name ||
      tenantLabelById.get(String(viewInvoice.client_id ?? "")) ||
      "";
    const payment = pickLatestPayment(paymentsByAr[viewInvoice.AR_Id] || []);
    return {
      emailHref: getReceiptShareEmailHref(
        viewInvoice,
        tenantLabel,
        resolveShareContactClient,
        issuerCompanies,
        payment
      ),
      whatsappHref: getReceiptShareWhatsAppHref(
        viewInvoice,
        tenantLabel,
        resolveShareContactClient,
        issuerCompanies,
        payment
      )
    };
  }, [viewInvoice, paymentsByAr, tenantLabelById, resolveShareContactClient, issuerCompanies]);

  const successReceiptShare = useMemo(() => {
    if (!paymentSuccess?.invoice) return { emailHref: null, whatsappHref: null };
    const inv = paymentSuccess.invoice;
    const tenantLabel =
      inv.Customer_Name || tenantLabelById.get(String(inv.client_id ?? "")) || "";
    const payment =
      paymentSuccess.payment || pickLatestPayment(paymentsByAr[inv.AR_Id] || []);
    // Reflect newly paid amount for share message when list not yet refreshed
    const invForShare = {
      ...inv,
      Paid_Amount: Math.max(num(inv.Paid_Amount), num(paymentSuccess.amount) || num(payment?.Amount)),
      Balance_Amount: Math.max(
        0,
        num(inv.Balance_Amount) - (num(paymentSuccess.amount) || num(payment?.Amount) || 0)
      )
    };
    return {
      emailHref: getReceiptShareEmailHref(
        invForShare,
        tenantLabel,
        resolveShareContactClient,
        issuerCompanies,
        payment
      ),
      whatsappHref: getReceiptShareWhatsAppHref(
        invForShare,
        tenantLabel,
        resolveShareContactClient,
        issuerCompanies,
        payment
      )
    };
  }, [paymentSuccess, paymentsByAr, tenantLabelById, resolveShareContactClient, issuerCompanies]);

  const colSpan = isFinance ? 13 : 12;

  return (
    <div className={`viewlead-section tenant-invoices-section${isFinance ? " finance-invoices-panel" : ""}`}>
      <div className={`viewlead-section-header${isFinance ? " viewlead-section-header--filters-only" : ""}`}>
        {!isFinance ? <h3>Invoices & Payments</h3> : null}
        <div className="viewlead-section-actions">
          {isFinance && (
            <>
              <select
                className="viewlead-search-input tenant-invoices-filter-select"
                value={filterClientId || ""}
                onChange={(e) => onFilterClientIdChange && onFilterClientIdChange(e.target.value)}
              >
                <option value="">Select a client…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {getTenantCompanyLabel(t)}
                  </option>
                ))}
              </select>
              <select
                className="viewlead-search-input tenant-invoices-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
              </select>
              <input
                type="text"
                className="viewlead-search-input"
                placeholder="Search invoices..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </>
          )}
          <button type="button" className="btn-add-followup" onClick={() => openGenerate()}>
            Generate Monthly Invoice
          </button>
        </div>
      </div>

      {error && !showGenerate && !showPayment && !reasonAction && (
        <p className="tenant-invoices-error">{error}</p>
      )}
      {notice && <p className="tenant-invoices-error">{notice}</p>}

      {loading ? (
        <p className="viewlead-no-data">Loading invoices...</p>
      ) : (
        <>
        <div className="tenant-invoices-table-wrap co-desktop-table">
          <table className="viewlead-table tenant-invoices-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                {isFinance && <th>Bill To</th>}
                <th>Date</th>
                <th>Billing Month</th>
                <th>Invoiced From</th>
                <th>Currency</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Generated by</th>
                <th>Updated by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="viewlead-no-data">
                    {listClientId
                      ? "No invoices yet. Generate a monthly invoice to get started."
                      : "Select a client to view its invoices."}
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((inv) => {
                  const arId = inv.AR_Id;
                  const expanded = expandedId === arId;
                  const pays = paymentsByAr[arId] || [];
                  const tenantLabel =
                    inv.Customer_Name ||
                    tenantLabelById.get(String(inv.client_id ?? "")) ||
                    "";
                  const dues = invoiceHasDues(inv);
                  const hasReceipt = invoiceHasReceipt(inv);
                  const accountsFrom = getInvoiceAccountsEmail(inv, issuerCompanies);
                  const contactClient = isFinance ? null : propClient;
                  const latestPay = pickLatestPayment(pays);
                  const emailHref = getInvoiceReminderEmailHref(
                    inv,
                    tenantLabel,
                    contactClient,
                    issuerCompanies
                  );
                  const whatsappHref = getInvoiceReminderWhatsAppHref(
                    inv,
                    tenantLabel,
                    contactClient,
                    issuerCompanies
                  );
                  const receiptEmailHref = getReceiptShareEmailHref(
                    inv,
                    tenantLabel,
                    contactClient,
                    issuerCompanies,
                    latestPay
                  );
                  const receiptWhatsappHref = getReceiptShareWhatsAppHref(
                    inv,
                    tenantLabel,
                    contactClient,
                    issuerCompanies,
                    latestPay
                  );
                  return (
                    <React.Fragment key={arId}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="table-name-link tenant-invoices-link-btn"
                            onClick={() => toggleExpand(arId)}
                          >
                            {inv.Invoice_No || "—"}
                          </button>
                        </td>
                        {isFinance && (
                          <td>
                            {inv.Bill_To?.Name ||
                              inv.Customer_Name ||
                              tenantLabelById.get(String(inv.client_id ?? "")) ||
                              "—"}
                            {inv.Bill_To?.GSTIN || inv.Bill_To?.Tax_Id ? (
                              <div className="tenant-invoices-item-desc">
                                {inv.Bill_To.GSTIN
                                  ? `GSTIN ${inv.Bill_To.GSTIN}`
                                  : `${inv.Bill_To.Tax_Id_Type || "Tax ID"} ${inv.Bill_To.Tax_Id}`}
                              </div>
                            ) : null}
                          </td>
                        )}
                        <td>{formatDateDisplay(inv.Invoice_Date)}</td>
                        <td>{parseBillingPeriod(inv.Notes) || "—"}</td>
                        <td>
                          {inv.Issuer_Company_Name || issuerLabel(inv.Issuer_Company) || "—"}
                        </td>
                        <td>{inv.Currency_Code || currencyLabel(inv.Currency_Id) || "—"}</td>
                        <td>{formatMoney(inv.Total_Amount)}</td>
                        <td>{formatMoney(inv.Paid_Amount)}</td>
                        <td>
                          {num(inv.Balance_Amount) > 0.001 ? (
                            <span className="tenant-invoices-balance-due">
                              {formatMoney(inv.Balance_Amount)}
                            </span>
                          ) : (
                            formatMoney(inv.Balance_Amount)
                          )}
                        </td>
                        <td>
                          <span
                            className={`status-badge status-${String(inv.Status || "")
                              .toLowerCase()
                              .replace(/\s/g, "-")}`}
                          >
                            {financeStatusLabel(inv)}
                          </span>
                        </td>
                        <td title={formatDateDisplay(inv.Created_On)}>
                          {displayUser(inv.Created_By_Name, inv.Created_By)}
                        </td>
                        <td title={formatDateDisplay(inv.Updated_On)}>
                          {displayUser(inv.Updated_By_Name, inv.Updated_By)}
                        </td>
                        <td>
                          <div className="tenant-invoices-actions">
                            <button
                              type="button"
                              className="tenant-invoices-action-btn"
                              title="View invoice"
                              aria-label="View invoice"
                              onClick={() => openView(inv)}
                            >
                              <FaEye />
                            </button>
                            <button
                              type="button"
                              className="tenant-invoices-action-btn"
                              title="Record payment"
                              aria-label="Record payment"
                              onClick={() => openPayment(inv)}
                              disabled={num(inv.Balance_Amount) <= 0 || inv.Status === "Cancelled"}
                            >
                              <FaMoneyBillWave />
                            </button>
                            <button
                              type="button"
                              className="tenant-invoices-action-btn"
                              title="Invoice PDF"
                              aria-label="Invoice PDF"
                              onClick={() => handleDownloadPdf(inv)}
                            >
                              <FaFileInvoiceDollar />
                            </button>
                            <button
                              type="button"
                              className="tenant-invoices-action-btn"
                              title={
                                hasReceipt
                                  ? "Receipt PDF (latest payment)"
                                  : "No payments yet — receipt unavailable"
                              }
                              aria-label="Receipt PDF"
                              onClick={() => handleDownloadReceiptPdf(inv)}
                              disabled={!hasReceipt}
                            >
                              <FaReceipt />
                            </button>
                            {canDeleteInvoice(inv) && (
                              <button
                                type="button"
                                className="tenant-invoices-action-btn"
                                title="Delete draft (reason required)"
                                aria-label="Delete draft"
                                onClick={() => openReasonDialog("delete", inv)}
                              >
                                <FaTrash />
                              </button>
                            )}
                            {canCancelInvoice(inv) && (
                              <button
                                type="button"
                                className="tenant-invoices-action-btn"
                                title="Cancel invoice (reason required)"
                                aria-label="Cancel invoice"
                                onClick={() => openReasonDialog("cancel", inv)}
                              >
                                <FaBan />
                              </button>
                            )}
                            {hasReceipt && (
                              <>
                                {receiptEmailHref ? (
                                  <a
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--receipt"
                                    href={receiptEmailHref}
                                    title={
                                      accountsFrom
                                        ? `Email receipt (BCC: ${accountsFrom})`
                                        : "Email receipt"
                                    }
                                    aria-label="Email receipt"
                                  >
                                    <FaEnvelope />
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--receipt"
                                    title="No email on file for this client"
                                    aria-label="Email receipt unavailable"
                                    disabled
                                  >
                                    <FaEnvelope />
                                  </button>
                                )}
                                {receiptWhatsappHref ? (
                                  <a
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--receipt"
                                    href={receiptWhatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="WhatsApp receipt"
                                    aria-label="WhatsApp receipt"
                                  >
                                    <FaWhatsapp />
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--receipt"
                                    title="No WhatsApp number on file (phone + country code required)"
                                    aria-label="WhatsApp receipt unavailable"
                                    disabled
                                  >
                                    <FaWhatsapp />
                                  </button>
                                )}
                              </>
                            )}
                            {dues && (
                              <>
                                {emailHref ? (
                                  <a
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--reminder"
                                    href={emailHref}
                                    title={
                                      accountsFrom
                                        ? `Email payment reminder (send From: ${accountsFrom})`
                                        : "Email payment reminder"
                                    }
                                    aria-label="Email payment reminder"
                                  >
                                    <FaEnvelope />
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--reminder"
                                    title="No email on file for this client"
                                    aria-label="Email reminder unavailable"
                                    disabled
                                  >
                                    <FaEnvelope />
                                  </button>
                                )}
                                {whatsappHref ? (
                                  <a
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--reminder"
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={
                                      accountsFrom
                                        ? `WhatsApp payment reminder (${accountsFrom})`
                                        : "WhatsApp payment reminder"
                                    }
                                    aria-label="WhatsApp payment reminder"
                                  >
                                    <FaWhatsapp />
                                  </a>
                                ) : (
                                  <button
                                    type="button"
                                    className="tenant-invoices-action-btn tenant-invoices-action-btn--reminder"
                                    title="No WhatsApp number on file (phone + country code required)"
                                    aria-label="WhatsApp reminder unavailable"
                                    disabled
                                  >
                                    <FaWhatsapp />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="tenant-invoices-expand-row">
                          <td colSpan={colSpan}>
                            <div className="tenant-invoices-payments-panel">
                              <strong>Payments</strong>
                              {loadingPayments[arId] ? (
                                <p className="viewlead-no-data">Loading payments...</p>
                              ) : pays.length === 0 ? (
                                <p className="viewlead-no-data">No payments recorded.</p>
                              ) : (
                                <table className="viewlead-table tenant-invoices-payments-table">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Amount</th>
                                      <th>Currency</th>
                                      <th>Mode</th>
                                      <th>Ref</th>
                                      <th>Status</th>
                                      <th>Recorded by</th>
                                      <th>Updated by</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pays.map((p) => (
                                      <tr key={p.Payment_Id}>
                                        <td>{formatDateDisplay(p.Payment_Date)}</td>
                                        <td>{formatMoney(p.Amount)}</td>
                                        <td>{p.Currency_Code || currencyLabel(p.Currency_Id) || "—"}</td>
                                        <td>{p.Payment_Mode_Name || "—"}</td>
                                        <td>{p.Transaction_Ref || "—"}</td>
                                        <td>{p.Payment_Status || "—"}</td>
                                        <td title={formatDateDisplay(p.Created_On)}>
                                          {displayUser(p.Created_By_Name, p.Created_By)}
                                        </td>
                                        <td title={formatDateDisplay(p.Updated_On)}>
                                          {displayUser(p.Updated_By_Name, p.Updated_By)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <ResultsMobileList
          emptyLabel={
            listClientId
              ? "No invoices yet. Generate a monthly invoice to get started."
              : "Select a client to view its invoices."
          }
          className="tenant-invoices-mobile"
        >
          {filteredInvoices.map((inv) => {
            const arId = inv.AR_Id;
            const tenantLabel =
              inv.Customer_Name ||
              tenantLabelById.get(String(inv.client_id ?? "")) ||
              "";
            const dues = invoiceHasDues(inv);
            const hasReceipt = invoiceHasReceipt(inv);
            const accountsFrom = getInvoiceAccountsEmail(inv, issuerCompanies);
            const contactClient = isFinance ? null : propClient;
            const latestPay = pickLatestPayment(paymentsByAr[arId] || []);
            const emailHref = getInvoiceReminderEmailHref(
              inv,
              tenantLabel,
              contactClient,
              issuerCompanies
            );
            const whatsappHref = getInvoiceReminderWhatsAppHref(
              inv,
              tenantLabel,
              contactClient,
              issuerCompanies
            );
            const receiptEmailHref = getReceiptShareEmailHref(
              inv,
              tenantLabel,
              contactClient,
              issuerCompanies,
              latestPay
            );
            const receiptWhatsappHref = getReceiptShareWhatsAppHref(
              inv,
              tenantLabel,
              contactClient,
              issuerCompanies,
              latestPay
            );
            const status = financeStatusLabel(inv);
            const statusTone = (() => {
              const v = String(status).toLowerCase();
              if (v === "paid") return "success";
              if (v === "cancelled" || v === "canceled" || v === "overdue") return "danger";
              if (v.includes("partial") || v === "sent" || v === "unpaid") return "warning";
              return "info";
            })();
            const cardTone =
              statusTone === "danger" ? "danger" : statusTone === "warning" ? "warning" : undefined;
            const canPay = num(inv.Balance_Amount) > 0 && inv.Status !== "Cancelled";

            return (
              <ResultsMobileCard
                key={arId}
                title={inv.Invoice_No || "—"}
                subtitle={isFinance ? tenantLabel || null : formatDateDisplay(inv.Invoice_Date)}
                tone={cardTone}
                meta={[{ label: status, tone: statusTone }]}
                fields={[
                  ...(isFinance
                    ? [{ label: "Date", value: formatDateDisplay(inv.Invoice_Date) }]
                    : []),
                  { label: "Billing", value: parseBillingPeriod(inv.Notes) },
                  ...(inv.Bill_To?.Name ? [{ label: "Bill To", value: inv.Bill_To.Name }] : []),
                  { label: "From", value: inv.Issuer_Company_Name || issuerLabel(inv.Issuer_Company) },
                  {
                    label: "Total",
                    value: `${inv.Currency_Code || currencyLabel(inv.Currency_Id) || ""} ${formatMoney(inv.Total_Amount)}`.trim(),
                  },
                  { label: "Paid", value: formatMoney(inv.Paid_Amount) },
                  {
                    label: "Balance",
                    value: formatMoney(inv.Balance_Amount),
                  },
                ]}
                onOpen={() => openView(inv)}
                actions={
                  <div className="co-mcard__action-grid">
                    <button
                      type="button"
                      className="co-maction"
                      title="View invoice"
                      aria-label="View invoice"
                      onClick={() => openView(inv)}
                    >
                      <FaEye size={14} />
                      <span>View</span>
                    </button>
                    <button
                      type="button"
                      className={`co-maction${canPay ? "" : " co-maction--disabled"}`}
                      title="Record payment"
                      aria-label="Record payment"
                      onClick={() => canPay && openPayment(inv)}
                      disabled={!canPay}
                    >
                      <FaMoneyBillWave size={14} />
                      <span>Pay</span>
                    </button>
                    <button
                      type="button"
                      className="co-maction"
                      title="Invoice PDF"
                      aria-label="Invoice PDF"
                      onClick={() => handleDownloadPdf(inv)}
                    >
                      <FaFileInvoiceDollar size={14} />
                      <span>Invoice</span>
                    </button>
                    <button
                      type="button"
                      className={`co-maction${hasReceipt ? "" : " co-maction--disabled"}`}
                      title={
                        hasReceipt
                          ? "Receipt PDF (latest payment)"
                          : "No payments yet — receipt unavailable"
                      }
                      aria-label="Receipt PDF"
                      onClick={() => hasReceipt && handleDownloadReceiptPdf(inv)}
                      disabled={!hasReceipt}
                    >
                      <FaReceipt size={14} />
                      <span>Receipt</span>
                    </button>
                    {hasReceipt ? (
                      <>
                        {receiptEmailHref ? (
                          <a
                            className="co-maction co-maction--receipt"
                            href={receiptEmailHref}
                            title={
                              accountsFrom
                                ? `Email receipt (BCC: ${accountsFrom})`
                                : "Email receipt"
                            }
                            aria-label="Email receipt"
                          >
                            <FaEnvelope size={14} />
                            <span>Receipt</span>
                          </a>
                        ) : (
                          <span className="co-maction co-maction--receipt co-maction--disabled" aria-disabled="true">
                            <FaEnvelope size={14} />
                            <span>Receipt</span>
                          </span>
                        )}
                        {receiptWhatsappHref ? (
                          <a
                            className="co-maction co-maction--receipt"
                            href={receiptWhatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="WhatsApp receipt"
                            aria-label="WhatsApp receipt"
                          >
                            <FaWhatsapp size={15} />
                            <span>Receipt</span>
                          </a>
                        ) : (
                          <span className="co-maction co-maction--receipt co-maction--disabled" aria-disabled="true">
                            <FaWhatsapp size={15} />
                            <span>Receipt</span>
                          </span>
                        )}
                      </>
                    ) : null}
                    {dues ? (
                      <>
                        {emailHref ? (
                          <a
                            className="co-maction co-maction--reminder"
                            href={emailHref}
                            title={
                              accountsFrom
                                ? `Email reminder (From: ${accountsFrom})`
                                : "Email reminder"
                            }
                            aria-label="Email reminder"
                          >
                            <FaEnvelope size={14} />
                            <span>Reminder</span>
                          </a>
                        ) : (
                          <span className="co-maction co-maction--reminder co-maction--disabled" aria-disabled="true">
                            <FaEnvelope size={14} />
                            <span>Reminder</span>
                          </span>
                        )}
                        {whatsappHref ? (
                          <a
                            className="co-maction co-maction--reminder"
                            href={whatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="WhatsApp reminder"
                            aria-label="WhatsApp reminder"
                          >
                            <FaWhatsapp size={15} />
                            <span>Reminder</span>
                          </a>
                        ) : (
                          <span className="co-maction co-maction--reminder co-maction--disabled" aria-disabled="true">
                            <FaWhatsapp size={15} />
                            <span>Reminder</span>
                          </span>
                        )}
                      </>
                    ) : null}
                  </div>
                }
              />
            );
          })}
        </ResultsMobileList>
        </>
      )}

      {/* Generate Monthly Invoice modal */}
      {showGenerate && (
        <div className="viewlead-modal-overlay" onClick={closeGenerate}>
          <div
            className="viewlead-modal-content tenant-invoices-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">
                {replaceSource ? `Replacement for ${replaceSource.Invoice_No}` : "Generate Monthly Invoice"}
              </h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={closeGenerate}
                disabled={submitting}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleGenerateSubmit} className="tenant-invoices-modal-body">
              {replaceSource ? (
                <p className="tenant-invoices-item-desc" style={{ marginBottom: 14 }}>
                  Pre-filled from cancelled invoice <strong>{replaceSource.Invoice_No}</strong>
                  {replaceSource.Cancel_Reason ? ` (reason: ${replaceSource.Cancel_Reason})` : ""}. Correct what was
                  wrong and create it; the new invoice gets its own number and its PDF shows which invoice it
                  replaces.
                </p>
              ) : null}
              {isFinance && (
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>
                    Client <span className="required">*</span>
                  </label>
                  <select
                    value={generateClientId}
                    onChange={async (e) => {
                      const v = e.target.value;
                      setGenerateClientId(v);
                      setBillToId("");
                      await loadClientForGenerate(v);
                    }}
                    disabled={!!replaceSource}
                    title={replaceSource ? "A replacement is for the same client as the cancelled invoice" : undefined}
                    required
                  >
                    <option value="">Select client</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {getTenantCompanyLabel(t)}
                      </option>
                    ))}
                  </select>
                  {loadingGenerateClient && (
                    <p className="viewlead-no-data" style={{ marginTop: 6 }}>
                      Loading client details...
                    </p>
                  )}
                </div>
              )}
              <div className="tenant-invoices-client-card">
                <h4>Client details</h4>
                <div className="tenant-invoices-client-grid">
                  <div>
                    <span className="tenant-invoices-label">Name</span>
                    <span>{clientDisplay.name}</span>
                  </div>
                  <div>
                    <span className="tenant-invoices-label">Company</span>
                    <span>{clientDisplay.company || "—"}</span>
                  </div>
                  <div>
                    <span className="tenant-invoices-label">Email</span>
                    <span>{clientDisplay.email || "—"}</span>
                  </div>
                  <div>
                    <span className="tenant-invoices-label">Phone</span>
                    <span>{clientDisplay.phone || "—"}</span>
                  </div>
                  <div className="tenant-invoices-client-full">
                    <span className="tenant-invoices-label">Address</span>
                    <span>{clientDisplay.address || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="tenant-invoices-form-grid">
                <div className="form-group">
                  <label>Billing month</label>
                  <select
                    value={billingMonth}
                    onChange={(e) => setBillingMonth(Number(e.target.value))}
                  >
                    {MONTH_NAMES.map((m, i) => (
                      <option key={m} value={i}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Billing year</label>
                  <select value={billingYear} onChange={(e) => setBillingYear(Number(e.target.value))}>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Invoice date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Due date</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>
                    From company <span className="required">*</span>
                  </label>
                  <div className="tenant-invoices-issuer-row">
                    <select
                      value={issuerCompanyId}
                      onChange={(e) => setIssuerCompanyId(e.target.value)}
                      disabled={loadingIssuers}
                      required
                    >
                      <option value="">{loadingIssuers ? "Loading..." : "Select company"}</option>
                      {issuerCompanies.map((c) => (
                        <option key={c.Issuer_Company_Id} value={c.Issuer_Company_Id}>
                          {issuerLabel(c)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="tenant-invoices-action-btn"
                      title="Edit company details"
                      aria-label="Edit company details"
                      onClick={openEditIssuer}
                      disabled={!issuerCompanyId}
                    >
                      <FaEdit />
                    </button>
                  </div>
                </div>
                <div className="form-group form-group-full">
                  <label>
                    Bill To <span className="required">*</span>
                  </label>
                  <BillToPicker
                    key={activeGenerateClientId || "none"}
                    clientId={activeGenerateClientId}
                    value={billToId}
                    onChange={(id) => setBillToId(id)}
                    disabled={!activeGenerateClientId || submitting}
                  />
                </div>
                <div className="form-group">
                  <label>
                    Currency <span className="required">*</span>
                  </label>
                  <select
                    value={currencyId}
                    onChange={(e) => setCurrencyId(e.target.value)}
                    disabled={loadingCurrencies}
                    required
                  >
                    <option value="">{loadingCurrencies ? "Loading..." : "Select currency"}</option>
                    {currencies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.symbol ? `${c.symbol} ` : ""}
                        {c.code || c.name || c.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tax %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Discount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              {selectedIssuer && (
                <div className="tenant-invoices-issuer-preview">
                  {selectedIssuer.Logo_Url ? (
                    <img
                      src={resolveImageUrl(selectedIssuer.Logo_Url)}
                      alt=""
                      className="tenant-invoices-issuer-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                  <div>
                    <strong>{issuerLabel(selectedIssuer)}</strong>
                    <div className="tenant-invoices-item-desc">
                      {[
                        selectedIssuer.Address_Line,
                        selectedIssuer.Phone,
                        selectedIssuer.Email,
                        selectedIssuer.Accounts_Email
                          ? `Accounts: ${selectedIssuer.Accounts_Email}`
                          : null
                      ]
                        .filter(Boolean)
                        .join(" · ") || "No address/contact yet — click edit to add."}
                    </div>
                  </div>
                </div>
              )}

              <h4 className="tenant-invoices-subtitle">Services</h4>
              {formServiceKeys.length === 0 ? (
                <p className="viewlead-no-data">No services assigned to this client. Add a custom line below.</p>
              ) : (
                <div className="tenant-invoices-services-list">
                  {formServiceKeys.map((name) => {
                    const row = selectedServices[name] || blankServiceRow();
                    return (
                      <div key={name} className="tenant-invoices-service-row">
                        <label className="tenant-invoices-service-check">
                          <input
                            type="checkbox"
                            checked={!!row.selected}
                            onChange={(e) =>
                              setSelectedServices((prev) => ({
                                ...prev,
                                [name]: { ...row, selected: e.target.checked }
                              }))
                            }
                          />
                          <span>{row.name || name}</span>
                        </label>
                        {row.selected && (
                          <div className="tenant-invoices-service-fields">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Qty"
                              value={row.qty}
                              onChange={(e) =>
                                setSelectedServices((prev) => ({
                                  ...prev,
                                  [name]: { ...row, qty: e.target.value }
                                }))
                              }
                            />
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Unit price"
                              value={row.price}
                              onChange={(e) =>
                                setSelectedServices((prev) => ({
                                  ...prev,
                                  [name]: { ...row, price: e.target.value }
                                }))
                              }
                              required
                            />
                            <input
                              type="text"
                              placeholder="Description (optional)"
                              value={row.description}
                              onChange={(e) =>
                                setSelectedServices((prev) => ({
                                  ...prev,
                                  [name]: { ...row, description: e.target.value }
                                }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="tenant-invoices-custom-line">
                <label className="tenant-invoices-service-check">
                  <input
                    type="checkbox"
                    checked={customLine.enabled}
                    onChange={(e) => setCustomLine((p) => ({ ...p, enabled: e.target.checked }))}
                  />
                  <span>Custom line item</span>
                </label>
                {customLine.enabled && (
                  <div className="tenant-invoices-service-fields">
                    <input
                      type="text"
                      placeholder="Service name"
                      value={customLine.name}
                      onChange={(e) => setCustomLine((p) => ({ ...p, name: e.target.value }))}
                      required
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Qty"
                      value={customLine.qty}
                      onChange={(e) => setCustomLine((p) => ({ ...p, qty: e.target.value }))}
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Unit price"
                      value={customLine.price}
                      onChange={(e) => setCustomLine((p) => ({ ...p, price: e.target.value }))}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={customLine.description}
                      onChange={(e) => setCustomLine((p) => ({ ...p, description: e.target.value }))}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Additional notes</label>
                <textarea
                  rows={2}
                  value={extraNotes}
                  onChange={(e) => setExtraNotes(e.target.value)}
                  placeholder="Optional notes (billing period is added automatically)"
                />
              </div>

              <div className="tenant-invoices-totals">
                <span>Subtotal: {formatMoney(totalsPreview.sub)}</span>
                <span>Tax: {formatMoney(totalsPreview.tax)}</span>
                <span>Discount: {formatMoney(totalsPreview.discount)}</span>
                <strong>
                  Total: {formatMoney(totalsPreview.total)}
                  {currencyLabel(currencyId) ? ` ${currencyLabel(currencyId)}` : ""}
                </strong>
              </div>

              {error && <p className="tenant-invoices-error">{error}</p>}

              <div className="viewlead-modal-actions">
                <button type="button" className="btn-secondary-lead" onClick={closeGenerate} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-edit-lead" disabled={submitting}>
                  {submitting ? "Creating..." : replaceSource ? "Create Replacement" : "Create Invoice"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment modal */}
      {showPayment && paymentInvoice && (
        <div
          className="viewlead-modal-overlay"
          onClick={() => !submitting && !receiptBusy && closePaymentModal()}
        >
          <div className="viewlead-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">
                {paymentSuccess ? "Payment recorded" : "Record Payment"}
              </h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={closePaymentModal}
                disabled={submitting || receiptBusy}
              >
                &times;
              </button>
            </div>
            {paymentSuccess ? (
              <div className="tenant-invoices-modal-body">
                <div className="tenant-invoices-pay-success">
                  <p className="tenant-invoices-pay-success__title">Payment saved successfully</p>
                  <p className="tenant-invoices-pay-meta">
                    <span className="tenant-invoices-pay-meta__item" title="Invoice">
                      <FaFileInvoiceDollar aria-hidden="true" />
                      <strong>{paymentSuccess.invoice?.Invoice_No || "—"}</strong>
                    </span>
                    <span className="tenant-invoices-pay-meta__sep" aria-hidden="true">
                      —
                    </span>
                    <span className="tenant-invoices-pay-meta__item" title="Amount paid">
                      <FaMoneyBillWave aria-hidden="true" />
                      <strong>
                        {formatMoney(paymentSuccess.amount)}
                        {paymentSuccess.receiptData?.payment?.currency
                          ? ` ${paymentSuccess.receiptData.payment.currency}`
                          : paymentSuccess.invoice?.Currency_Code
                            ? ` ${paymentSuccess.invoice.Currency_Code}`
                            : ""}
                      </strong>
                    </span>
                  </p>
                  <p className="tenant-invoices-pay-success__hint">
                    {paymentSuccess.receiptData
                      ? "Receipt and invoice PDFs have been downloaded."
                      : "Payment saved. You can download the PDFs below."}
                  </p>
                </div>
                <div className="viewlead-modal-actions tenant-invoices-pay-success-actions">
                  <button
                    type="button"
                    className="btn-secondary-lead"
                    disabled={receiptBusy}
                    onClick={async () => {
                      setReceiptBusy(true);
                      setError("");
                      try {
                        const receiptData = await downloadReceiptForPayment(
                          paymentSuccess.invoice,
                          paymentSuccess.payment,
                          paymentSuccess.amount
                        );
                        setPaymentSuccess((prev) => (prev ? { ...prev, receiptData } : prev));
                      } catch (err) {
                        console.error(err);
                        setError("Failed to download receipt.");
                      } finally {
                        setReceiptBusy(false);
                      }
                    }}
                  >
                    <FaFilePdf style={{ marginRight: 6 }} />
                    {receiptBusy ? "Preparing…" : "Receipt PDF"}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary-lead"
                    disabled={receiptBusy}
                    onClick={async () => {
                      setReceiptBusy(true);
                      setError("");
                      try {
                        await downloadInvoiceForPayment(
                          paymentSuccess.invoice,
                          paymentSuccess.payment,
                          paymentSuccess.amount
                        );
                        setPaymentSuccess((prev) =>
                          prev ? { ...prev, invoiceDownloaded: true } : prev
                        );
                      } catch (err) {
                        console.error(err);
                        setError("Failed to download invoice.");
                      } finally {
                        setReceiptBusy(false);
                      }
                    }}
                  >
                    <FaFileInvoiceDollar style={{ marginRight: 6 }} />
                    {receiptBusy ? "Preparing…" : "Invoice PDF"}
                  </button>
                  {successReceiptShare.emailHref ? (
                    <a className="btn-secondary-lead" href={successReceiptShare.emailHref}>
                      <FaEnvelope style={{ marginRight: 6 }} />
                      Email receipt
                    </a>
                  ) : null}
                  {successReceiptShare.whatsappHref ? (
                    <a
                      className="btn-secondary-lead"
                      href={successReceiptShare.whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaWhatsapp style={{ marginRight: 6 }} />
                      WhatsApp receipt
                    </a>
                  ) : null}
                  <button type="button" className="btn-edit-lead" onClick={closePaymentModal}>
                    Done
                  </button>
                </div>
                {error && <p className="tenant-invoices-error">{error}</p>}
              </div>
            ) : (
            <form onSubmit={handlePaymentSubmit} className="tenant-invoices-modal-body">
              <p className="tenant-invoices-pay-meta">
                <span className="tenant-invoices-pay-meta__item" title="Invoice">
                  <FaFileInvoiceDollar aria-hidden="true" />
                  <strong>{paymentInvoice.Invoice_No}</strong>
                </span>
                <span className="tenant-invoices-pay-meta__sep" aria-hidden="true">
                  —
                </span>
                <span className="tenant-invoices-pay-meta__item" title="Balance">
                  <FaMoneyBillWave aria-hidden="true" />
                  <strong>
                    {formatMoney(paymentInvoice.Balance_Amount)}
                    {paymentInvoice.Currency_Code || currencyLabel(paymentInvoice.Currency_Id)
                      ? ` ${paymentInvoice.Currency_Code || currencyLabel(paymentInvoice.Currency_Id)}`
                      : ""}
                  </strong>
                </span>
              </p>
              <div className="tenant-invoices-form-grid">
                <div className="form-group">
                  <label>
                    Amount <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={payForm.amount}
                    onChange={(e) => setPayForm((p) => ({ ...p, amount: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    Payment date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    value={payForm.paymentDate}
                    onChange={(e) => setPayForm((p) => ({ ...p, paymentDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select
                    value={payForm.currencyId}
                    onChange={(e) => setPayForm((p) => ({ ...p, currencyId: e.target.value }))}
                    disabled={loadingCurrencies}
                  >
                    <option value="">{loadingCurrencies ? "Loading..." : "Select currency"}</option>
                    {currencies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.symbol ? `${c.symbol} ` : ""}
                        {c.code || c.name || c.id}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment mode</label>
                  <select
                    value={payForm.paymentModeId}
                    onChange={(e) => setPayForm((p) => ({ ...p, paymentModeId: e.target.value }))}
                  >
                    <option value="">Select mode</option>
                    {paymentModes.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Transaction ref</label>
                  <input
                    type="text"
                    value={payForm.transactionRef}
                    onChange={(e) => setPayForm((p) => ({ ...p, transactionRef: e.target.value }))}
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Notes</label>
                  <textarea
                    rows={2}
                    value={payForm.notes}
                    onChange={(e) => setPayForm((p) => ({ ...p, notes: e.target.value }))}
                  />
                </div>
              </div>
              {error && <p className="tenant-invoices-error">{error}</p>}
              <div className="viewlead-modal-actions">
                <button
                  type="button"
                  className="btn-secondary-lead"
                  onClick={closePaymentModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-edit-lead" disabled={submitting || receiptBusy}>
                  {submitting || receiptBusy ? "Saving..." : "Save & Receipt"}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {/* View invoice modal */}
      {viewInvoice && (
        <div className="viewlead-modal-overlay" onClick={() => setViewInvoice(null)}>
          <div
            className="viewlead-modal-content tenant-invoices-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Invoice {viewInvoice.Invoice_No}</h3>
              <button type="button" className="viewlead-modal-close" onClick={() => setViewInvoice(null)}>
                &times;
              </button>
            </div>
            <div className="tenant-invoices-modal-body">
              <div className="tenant-invoices-client-grid">
                <div>
                  <span className="tenant-invoices-label">Date</span>
                  <span>{formatDateDisplay(viewInvoice.Invoice_Date)}</span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Due</span>
                  <span>{formatDateDisplay(viewInvoice.Due_Date)}</span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Period</span>
                  <span>{parseBillingPeriod(viewInvoice.Notes) || "—"}</span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Currency</span>
                  <span>
                    {viewInvoice.Currency_Code || currencyLabel(viewInvoice.Currency_Id) || "—"}
                  </span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Invoiced From</span>
                  <span>
                    {viewInvoice.Issuer_Company_Name ||
                      issuerLabel(viewInvoice.Issuer_Company) ||
                      "—"}
                  </span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Status</span>
                  <span>{viewInvoice.Status || "—"}</span>
                </div>
                <div className="tenant-invoices-client-full">
                  <span className="tenant-invoices-label">Bill To</span>
                  <span>
                    {viewInvoice.Bill_To?.Name || viewInvoice.Customer_Name || "—"}
                    {viewInvoice.Bill_To?.Contact ? ` · ${viewInvoice.Bill_To.Contact}` : ""}
                    {viewInvoice.Bill_To?.Address ? (
                      <div className="tenant-invoices-item-desc">{viewInvoice.Bill_To.Address}</div>
                    ) : null}
                    {billToTaxLine(viewInvoice.Bill_To) ? (
                      <div className="tenant-invoices-item-desc">{billToTaxLine(viewInvoice.Bill_To)}</div>
                    ) : null}
                  </span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Generated by</span>
                  <span>
                    {displayUser(viewInvoice.Created_By_Name, viewInvoice.Created_By)}
                    {viewInvoice.Created_On ? ` · ${formatDateDisplay(viewInvoice.Created_On)}` : ""}
                  </span>
                </div>
                <div>
                  <span className="tenant-invoices-label">Updated by</span>
                  <span>
                    {displayUser(viewInvoice.Updated_By_Name, viewInvoice.Updated_By)}
                    {viewInvoice.Updated_On ? ` · ${formatDateDisplay(viewInvoice.Updated_On)}` : ""}
                  </span>
                </div>
                {viewInvoice.Status === "Cancelled" ? (
                  <div className="tenant-invoices-client-full">
                    <span className="tenant-invoices-label">Cancelled</span>
                    <span>
                      {displayUser(viewInvoice.Cancelled_By_Name, viewInvoice.Cancelled_By)}
                      {viewInvoice.Cancelled_On ? ` · ${formatDateDisplay(viewInvoice.Cancelled_On)}` : ""}
                      {viewInvoice.Cancel_Reason ? (
                        <div className="tenant-invoices-item-desc">Reason: {viewInvoice.Cancel_Reason}</div>
                      ) : null}
                      {viewInvoice.Replaced_By_Invoice_No ? (
                        <div className="tenant-invoices-item-desc">
                          Replaced by {viewInvoice.Replaced_By_Invoice_No}
                        </div>
                      ) : null}
                    </span>
                  </div>
                ) : null}
                {viewInvoice.Replaces_Invoice_No ? (
                  <div className="tenant-invoices-client-full">
                    <span className="tenant-invoices-label">Replaces</span>
                    <span>{viewInvoice.Replaces_Invoice_No} (cancelled)</span>
                  </div>
                ) : null}
              </div>
              {Array.isArray(viewInvoice.items) && viewInvoice.items.length > 0 && (
                <table className="viewlead-table tenant-invoices-payments-table">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.items.map((it) => (
                      <tr key={it.Item_Id}>
                        <td>
                          {it.Service_Name || "—"}
                          {it.Description ? (
                            <div className="tenant-invoices-item-desc">{it.Description}</div>
                          ) : null}
                        </td>
                        <td>{it.Quantity}</td>
                        <td>{formatMoney(it.Unit_Price)}</td>
                        <td>{formatMoney(it.Total_Amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="tenant-invoices-totals">
                <span>Total: {formatMoney(viewInvoice.Total_Amount)}</span>
                <span>Paid: {formatMoney(viewInvoice.Paid_Amount)}</span>
                <strong>Balance: {formatMoney(viewInvoice.Balance_Amount)}</strong>
              </div>
              <div className="viewlead-modal-actions">
                {num(viewInvoice.Balance_Amount) > 0 && viewInvoice.Status !== "Cancelled" ? (
                  <button
                    type="button"
                    className="btn-edit-lead"
                    onClick={() => {
                      const inv = viewInvoice;
                      setViewInvoice(null);
                      openPayment(inv);
                    }}
                  >
                    Record Payment
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn-secondary-lead"
                  onClick={() => handleDownloadPdf(viewInvoice)}
                >
                  <FaFileInvoiceDollar style={{ marginRight: 6 }} />
                  Invoice PDF
                </button>
                {viewInvoice.Issued_Pdf?.Download_Url ? (
                  <button
                    type="button"
                    className="btn-secondary-lead"
                    title="The copy saved when this invoice was issued"
                    onClick={() => openStoredInvoicePdf(viewInvoice.Issued_Pdf)}
                  >
                    <FaFilePdf style={{ marginRight: 6 }} />
                    Issued copy
                  </button>
                ) : null}
                {viewInvoice.Cancelled_Pdf?.Download_Url ? (
                  <button
                    type="button"
                    className="btn-secondary-lead"
                    title="The copy saved when this invoice was cancelled"
                    onClick={() => openStoredInvoicePdf(viewInvoice.Cancelled_Pdf)}
                  >
                    <FaFilePdf style={{ marginRight: 6 }} />
                    Cancelled copy
                  </button>
                ) : null}
                {canDeleteInvoice(viewInvoice) ? (
                  <button
                    type="button"
                    className="btn-secondary-lead"
                    onClick={() => openReasonDialog("delete", viewInvoice)}
                  >
                    <FaTrash style={{ marginRight: 6 }} />
                    Delete draft
                  </button>
                ) : null}
                {canCancelInvoice(viewInvoice) ? (
                  <button
                    type="button"
                    className="btn-secondary-lead"
                    onClick={() => openReasonDialog("cancel", viewInvoice)}
                  >
                    <FaBan style={{ marginRight: 6 }} />
                    Cancel invoice
                  </button>
                ) : null}
                {viewInvoice.Status === "Cancelled" && !viewInvoice.Replaced_By_AR_Id ? (
                  <button type="button" className="btn-edit-lead" onClick={() => openReplacement(viewInvoice)}>
                    Create replacement
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn-secondary-lead"
                  onClick={() => handleDownloadReceiptPdf(viewInvoice)}
                  disabled={!invoiceHasReceipt(viewInvoice)}
                  title={
                    invoiceHasReceipt(viewInvoice)
                      ? "Receipt PDF (latest payment)"
                      : "No payments yet — receipt unavailable"
                  }
                >
                  <FaReceipt style={{ marginRight: 6 }} />
                  Receipt PDF
                </button>
                {viewReceiptShare.emailHref ? (
                  <a className="btn-secondary-lead" href={viewReceiptShare.emailHref}>
                    <FaEnvelope style={{ marginRight: 6 }} />
                    Email receipt
                  </a>
                ) : null}
                {viewReceiptShare.whatsappHref ? (
                  <a
                    className="btn-secondary-lead"
                    href={viewReceiptShare.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp style={{ marginRight: 6 }} />
                    WhatsApp receipt
                  </a>
                ) : null}
                <button type="button" className="btn-secondary-lead" onClick={() => setViewInvoice(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete draft / cancel invoice: reason required */}
      {reasonAction && (
        <div className="viewlead-modal-overlay" onClick={closeReasonDialog} style={{ zIndex: 1100 }}>
          <div className="viewlead-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">
                {reasonAction.type === "delete" ? "Delete draft" : "Cancel invoice"}{" "}
                {reasonAction.invoice?.Invoice_No || ""}
              </h3>
              <button type="button" className="viewlead-modal-close" onClick={closeReasonDialog} disabled={submitting}>
                &times;
              </button>
            </div>
            <form onSubmit={handleReasonSubmit} className="tenant-invoices-modal-body">
              <p className="tenant-invoices-item-desc">
                {reasonAction.type === "delete"
                  ? "The draft is hidden from all lists. Its invoice number is not reused, and the reason is kept for audit."
                  : "The invoice stays on record as Cancelled, the client portal shows it as cancelled, and a PDF copy stamped CANCELLED is saved."}
              </p>
              <div className="form-group">
                <label>
                  Reason <span className="required">*</span>
                </label>
                <textarea
                  rows={3}
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder={
                    reasonAction.type === "delete"
                      ? "e.g. Created by mistake"
                      : "e.g. Raised against the wrong client; replaced by a new invoice"
                  }
                  required
                  autoFocus
                />
              </div>
              {reasonAction.type === "cancel" ? (
                <label className="tenant-invoices-service-check" style={{ marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={replaceAfterCancel}
                    onChange={(e) => setReplaceAfterCancel(e.target.checked)}
                    disabled={submitting}
                  />
                  <span>Create a replacement invoice (pre-filled from this one) after cancelling</span>
                </label>
              ) : null}
              {error && <p className="tenant-invoices-error">{error}</p>}
              <div className="viewlead-modal-actions">
                <button type="button" className="btn-secondary-lead" onClick={closeReasonDialog} disabled={submitting}>
                  Back
                </button>
                <button type="submit" className="btn-edit-lead" disabled={submitting || !reasonText.trim()}>
                  {submitting
                    ? "Saving..."
                    : reasonAction.type === "delete"
                      ? "Delete draft"
                      : "Cancel invoice"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit issuer company (logo, address, contact) */}
      {showEditIssuer && (
        <div
          className="viewlead-modal-overlay"
          onClick={() => !submitting && setShowEditIssuer(false)}
          style={{ zIndex: 1100 }}
        >
          <div className="viewlead-modal-content tenant-invoices-modal" onClick={(e) => e.stopPropagation()}>
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Edit company details</h3>
              <button
                type="button"
                className="viewlead-modal-close"
                onClick={() => setShowEditIssuer(false)}
                disabled={submitting}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveIssuer} className="tenant-invoices-modal-body">
              <div className="tenant-invoices-form-grid">
                <div className="form-group">
                  <label>
                    Company name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={issuerEditForm.Company_Name}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Company_Name: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Display name</label>
                  <input
                    type="text"
                    value={issuerEditForm.Display_Name}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Display_Name: e.target.value }))}
                    placeholder="Name shown on invoice"
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Address</label>
                  <textarea
                    rows={2}
                    value={issuerEditForm.Address_Line}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Address_Line: e.target.value }))}
                    placeholder="Street, city, state, PIN"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={issuerEditForm.Phone}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Phone: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={issuerEditForm.Email}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Email: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Accounts email (reminder From)</label>
                  <input
                    type="email"
                    value={issuerEditForm.Accounts_Email}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Accounts_Email: e.target.value }))}
                    placeholder="accounts@digixleads.com"
                  />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="text"
                    value={issuerEditForm.Website}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Website: e.target.value }))}
                  />
                </div>
                <div className="form-group form-group-full">
                  <label>Logo URL</label>
                  <input
                    type="text"
                    value={issuerEditForm.Logo_Url}
                    onChange={(e) => setIssuerEditForm((p) => ({ ...p, Logo_Url: e.target.value }))}
                    placeholder="https://... or /uploads/..."
                  />
                  {issuerEditForm.Logo_Url ? (
                    <img
                      src={resolveImageUrl(issuerEditForm.Logo_Url)}
                      alt="Logo preview"
                      className="tenant-invoices-issuer-logo-preview"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null}
                </div>
              </div>
              {error && <p className="tenant-invoices-error">{error}</p>}
              <div className="viewlead-modal-actions">
                <button
                  type="button"
                  className="btn-secondary-lead"
                  onClick={() => setShowEditIssuer(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-edit-lead" disabled={submitting}>
                  {submitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantInvoicesTab;
